import { Component, OnInit, OnDestroy, HostListener, inject, computed, signal } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { interval, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { HeaderComponent } from './core/ui/header/header.component';
import { SidebarComponent } from './core/ui/sidebar/sidebar.component';
import { BreadcrumbComponent } from './core/shared/components/breadcrumb/breadcrumb.component';
import { SpinnerComponent } from './core/shared/components/spinner/spinner.component';
import { AppLoaderComponent } from './core/shared/components/app-loader/app-loader.component';

import { AuthViewModel } from './features/auth/viewmodels/auth.viewmodel';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    SidebarComponent,
    HeaderComponent,
    BreadcrumbComponent,
    SpinnerComponent,
    AppLoaderComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Sistema de Operações com Participantes - PREVI';

  private readonly _destroying$ = new Subject<void>();
  readonly authViewModel = inject(AuthViewModel);
  private readonly router = inject(Router);

  // Controla se a verificação inicial de autenticação já foi concluída.
  // Inicia false para que o AppLoaderComponent seja exibido imediatamente.
  readonly isAppInitialized = signal(false);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => (event as NavigationEnd).urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly isLoginRoute = computed(() => {
    const url = this.currentUrl();
    return url === '/' || url === '/login' || url?.startsWith('/login?');
  });

  readonly isAccessDeniedRoute = computed(() => {
    const url = this.currentUrl();
    return url === '/acesso-negado' || url?.startsWith('/acesso-negado?');
  });

  readonly isStandaloneRoute = computed(() =>
    this.isLoginRoute() || this.isAccessDeniedRoute()
  );

  ngOnInit(): void {

    // A autenticação não pode bloquear a renderização do Router. Os guards
    // aguardam o sinal de inicialização quando a rota exige autenticação.
    // Em uma primeira visita sem sessão, isso permite renderizar o login
    // mesmo que a inicialização do MSAL demore ou falhe.
    void this.authViewModel.initialize().finally(() => {
      this.startPeriodicSessionValidation();
    });
    this.isAppInitialized.set(true);
  }

  private startPeriodicSessionValidation(): void {
    interval(5 * 60 * 1000)
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        if (this.authViewModel.isAuthenticated()) {
          void this.authViewModel.validateSession();
        }
      });
  }

  /** Revalida uma sessão que ficou inativa enquanto a aba estava aberta. */
  @HostListener('window:focus')
  async onWindowFocus(): Promise<void> {
    if (this.isAppInitialized() && this.authViewModel.isAuthenticated()) {
      try {
        await this.authViewModel.validateSession();
      } catch {
        // A navegação para login é feita pelo ViewModel quando a sessão é inválida.
      }
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
